import React, { useState } from "react";
import { Link } from "react-router-dom";
import { PROJECTS, INTERVENTION_AREAS } from "@/lib/content";

const isVideo = (src) => typeof src === "string" && src.toLowerCase().endsWith(".mp4");

export default function Realisations() {
  const [lightbox, setLightbox] = useState(null);
  const [photoIdx, setPhotoIdx] = useState(0);

  const openLightbox = (p) => {
    setLightbox(p);
    setPhotoIdx(0);
  };
  const nextPhoto = (e) => {
    e.stopPropagation();
    if (!lightbox) return;
    setPhotoIdx((i) => (i + 1) % lightbox.gallery.length);
  };
  const prevPhoto = (e) => {
    e.stopPropagation();
    if (!lightbox) return;
    setPhotoIdx((i) => (i - 1 + lightbox.gallery.length) % lightbox.gallery.length);
  };

  return (
    <div className="bg-[#0a0a0a] text-white pt-32 pb-24" data-testid="realisations-page">
      <section className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="eyebrow mb-4">/ Réalisations</div>
        <h1 className="font-display font-bold uppercase tracking-tighter text-5xl md:text-7xl leading-[0.92]">
          Chantiers livrés.<br /><span className="text-[#ffd600]">Sans surprise.</span>
        </h1>
        <p className="text-neutral-400 mt-6 text-lg max-w-3xl">
          Sélection de chantiers neufs réalisés par DCR-BATI en Île-de-France. Du coffrage à la dalle de toiture, chaque projet illustre notre maîtrise du gros œuvre béton armé.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
          {PROJECTS.map((p) => (
            <article
              key={p.name}
              className="group relative border border-[#262626] bg-[#141414] cursor-pointer overflow-hidden"
              onClick={() => openLightbox(p)}
              data-testid={`project-card-${p.location}`}
            >
              <div className="relative h-80 overflow-hidden">
                {p.video ? (
                  <video
                    src={p.video}
                    poster={p.image}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    data-testid={`project-video-${p.location}`}
                  />
                ) : (
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute top-3 left-3 bg-[#ffd600] text-black font-display uppercase text-[10px] tracking-widest px-2 py-1">{p.tag}</div>
                {p.video && (
                  <div className="absolute top-3 right-3 bg-black/70 border border-white/10 text-white font-mono-tech text-[10px] tracking-widest px-2 py-1 flex items-center gap-1">
                    <i className="fa-solid fa-video" /> Vidéo
                  </div>
                )}
                {!p.video && p.gallery && p.gallery.length > 1 && (
                  <div className="absolute top-3 right-3 bg-black/70 border border-white/10 text-white font-mono-tech text-[10px] tracking-widest px-2 py-1">
                    <i className="fa-solid fa-images mr-1" /> {p.gallery.length}
                  </div>
                )}
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-300">{p.location}</div>
                  <div className="font-display uppercase text-2xl font-semibold mt-1 leading-tight">{p.name}</div>
                </div>
              </div>
              <div className="px-4 py-3 flex items-center justify-between border-t border-white/5">
                <div className="text-sm text-neutral-400">{p.type}</div>
                <div className="font-display uppercase tracking-wider text-[#ffd600] text-sm">{p.surface}</div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Zone d'intervention */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 mt-24" data-testid="intervention-zone">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center border border-[#262626] bg-[#0d0d0d] p-8 md:p-12 relative overflow-hidden">
          <div className="hazard-stripes absolute top-0 left-0 right-0 h-1.5" />
          <div className="md:col-span-7">
            <div className="eyebrow mb-3">/ Zone d&apos;intervention</div>
            <h2 className="font-display font-bold uppercase tracking-tight text-3xl md:text-5xl leading-[0.95]">
              Île-de-France.<br /><span className="text-[#ffd600]">Tous départements.</span>
            </h2>
            <p className="text-neutral-400 mt-5 leading-relaxed max-w-xl">
              Basés à Noisy-le-Grand (93), nos équipes interviennent sur l&apos;ensemble de la région parisienne. Logistique optimisée, déplacements courts, réactivité maximale pour vos chantiers franciliens.
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="grid grid-cols-2 gap-2">
              {INTERVENTION_AREAS.map((a) => (
                <div key={a.code} className="flex items-center gap-3 border border-[#262626] bg-[#141414] px-4 py-3" data-testid={`area-${a.code}`}>
                  <div className="font-display text-2xl text-[#ffd600] font-semibold leading-none w-10">{a.code}</div>
                  <div className="font-mono-tech text-xs uppercase tracking-widest text-neutral-300">{a.name}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
          data-testid="lightbox"
        >
          <div className="max-w-5xl w-full bg-[#0a0a0a] border border-[#262626] relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setLightbox(null)} className="absolute top-3 right-3 z-10 bg-black/80 border border-white/10 w-10 h-10 flex items-center justify-center text-white hover:border-[#ffd600] hover:text-[#ffd600]" data-testid="lightbox-close">
              <i className="fa-solid fa-xmark" />
            </button>
            <div className="relative">
              {isVideo(lightbox.gallery[photoIdx]) ? (
                <video
                  src={lightbox.gallery[photoIdx]}
                  className="w-full max-h-[65vh] object-contain bg-black"
                  autoPlay
                  loop
                  muted
                  playsInline
                  controls
                  data-testid="lightbox-video"
                />
              ) : (
                <img src={lightbox.gallery[photoIdx]} alt={lightbox.name} className="w-full max-h-[65vh] object-cover" />
              )}
              {lightbox.gallery.length > 1 && (
                <>
                  <button onClick={prevPhoto} className="absolute top-1/2 -translate-y-1/2 left-3 bg-black/80 border border-white/10 w-11 h-11 flex items-center justify-center text-white hover:border-[#ffd600] hover:text-[#ffd600]" data-testid="lightbox-prev">
                    <i className="fa-solid fa-chevron-left" />
                  </button>
                  <button onClick={nextPhoto} className="absolute top-1/2 -translate-y-1/2 right-3 bg-black/80 border border-white/10 w-11 h-11 flex items-center justify-center text-white hover:border-[#ffd600] hover:text-[#ffd600]" data-testid="lightbox-next">
                    <i className="fa-solid fa-chevron-right" />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/80 border border-white/10 px-3 py-1 font-mono-tech text-xs tracking-widest">
                    {photoIdx + 1} / {lightbox.gallery.length}
                  </div>
                </>
              )}
            </div>
            <div className="p-6">
              <div className="font-mono-tech text-xs uppercase tracking-widest text-[#ffd600]">{lightbox.location} · {lightbox.type}</div>
              <h3 className="font-display uppercase text-2xl mt-2">{lightbox.name}</h3>
              <p className="text-neutral-300 mt-3 leading-relaxed">{lightbox.description}</p>
              <div className="grid grid-cols-3 gap-4 mt-5 text-sm">
                <div><div className="text-neutral-500 text-xs uppercase tracking-widest">Prestations</div><div className="text-[#ffd600] font-display text-lg mt-1">{lightbox.surface}</div></div>
                <div><div className="text-neutral-500 text-xs uppercase tracking-widest">Statut</div><div className="text-white font-display text-lg mt-1">{lightbox.tag}</div></div>
                <div><div className="text-neutral-500 text-xs uppercase tracking-widest">Localisation</div><div className="text-white font-display text-lg mt-1">{lightbox.location}</div></div>
              </div>
              {lightbox.ctaService && (
                <div className="mt-6 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="text-neutral-300 text-sm max-w-md">
                    Un projet similaire ? Demandez un devis gratuit et sans engagement.
                  </div>
                  <Link
                    to={`/devis?service=${lightbox.ctaService}`}
                    onClick={() => setLightbox(null)}
                    className="inline-flex items-center gap-2 bg-[#ffd600] text-black font-display uppercase tracking-wider text-sm px-6 py-3 hover:bg-white transition-colors"
                    data-testid="lightbox-cta-devis"
                  >
                    Demander un devis
                    <i className="fa-solid fa-arrow-right" />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
